import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../../../../../../../enums/notificationKeys'
import { APIError } from '../../../../../../../../../utils'
import { useValidateAccountNumber } from '../../../../../hooks/useValidateAccountNumber'
import { ValidateAccountNumberPayload } from '../../../../../query-api'
import { shouldValidateAccountNumber } from '../../../utils/accountNumberValidation'
import { getAccountNumberValidationMessage } from '../../../utils/getAccountNumberValidationMessage'

interface UseAccountNumberValidationOptions {
  automaticAccountNumber: number | undefined
  clearAccountNumberError: () => void
  getAccountNumber: () => number | null
  isManualEntryEnabled: boolean
  organizationId: string | undefined
  setAccountNumberError: (message: string) => void
}

export const useAccountNumberValidation = (options: UseAccountNumberValidationOptions) => {
  const {
    automaticAccountNumber,
    clearAccountNumberError,
    getAccountNumber,
    isManualEntryEnabled,
    organizationId,
    setAccountNumberError,
  } = options
  const { t } = useTranslation()

  const handleValidationSuccess = useCallback(
    (isAvailable: boolean, payload: ValidateAccountNumberPayload) => {
      if (getAccountNumber() !== payload.accountNumber) {
        return
      }

      if (isAvailable) {
        clearAccountNumberError()
        return
      }

      setAccountNumberError(t('coa.account_from_base_form.account_number.invalid'))
    },
    [clearAccountNumberError, getAccountNumber, setAccountNumberError, t],
  )

  const handleValidationError = useCallback(
    (error: APIError, payload: ValidateAccountNumberPayload) => {
      if (getAccountNumber() !== payload.accountNumber) {
        return
      }

      const fieldErrorMessage = getAccountNumberValidationMessage(error)
      if (fieldErrorMessage) {
        setAccountNumberError(fieldErrorMessage)
        return
      }

      notify({
        id: NotificationKeys.AccountFromTemplateCreate,
        message: error.body?.errorMessage || t('error_saving'),
        variant: 'error',
      })
      trackError(error)
    },
    [getAccountNumber, setAccountNumberError, t],
  )

  const { isProcessing, isSuccess, resetValidation, validateAccountNumberAsync, validatedPayload, validationResult } =
    useValidateAccountNumber({
      onError: handleValidationError,
      onSuccess: handleValidationSuccess,
    })

  const clearValidation = useCallback(() => {
    resetValidation()
    clearAccountNumberError()
  }, [clearAccountNumberError, resetValidation])

  const validateAccountNumberIfNeeded = useCallback(
    async (accountNumber: number | null): Promise<boolean> => {
      const validationOptions = {
        accountNumber,
        automaticAccountNumber,
        isManualEntryEnabled,
      }

      if (!shouldValidateAccountNumber(validationOptions)) {
        const isValidAccount = accountNumber !== null

        if (isValidAccount) {
          clearAccountNumberError()
        }

        return isValidAccount
      }

      if (!organizationId || isProcessing) {
        return false
      }

      if (isSuccess && validatedPayload?.accountNumber === accountNumber) {
        return Boolean(validationResult)
      }

      try {
        return await validateAccountNumberAsync({ accountNumber: validationOptions.accountNumber, organizationId })
      } catch {
        return false
      }
    },
    [
      automaticAccountNumber,
      clearAccountNumberError,
      isManualEntryEnabled,
      isProcessing,
      isSuccess,
      organizationId,
      validateAccountNumberAsync,
      validatedPayload?.accountNumber,
      validationResult,
    ],
  )

  return {
    clearValidation,
    isProcessing,
    validateAccountNumberIfNeeded,
  }
}
